"use client";

import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import {
  ChevronLeft,
  Clock,
  Loader2,
  Calendar,
  FileText,
} from "lucide-react";
import { useAuth } from "@workos-inc/authkit-react";
import api from "../http";
import SettingsDropdown from "../components/assistant/SettingsDropdown";
import ProfileImageModal from "../components/assistant/ProfileImageModal";
import { useColor } from "@/contexts/ColorContext";
import { appConfig } from "@/appConfig";
import { useToast } from "@/hooks/use-toast";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface TranscriptSegment {
  id?: string;
  start?: number;
  end?: number;
  text: string;
  speaker?: string;
}

interface Transcript {
  text: string;
  duration?: number;
  segments: TranscriptSegment[];
}

interface MeetingNote {
  id: string;
  title: string;
  template_id: string | null;
  transcript?: Transcript;
  generated_note?: string;
  audio_duration: number | null;
  created_by_id: string;
  created_at: string;
  updated_at: string;
}

interface MeetingTemplate {
  id: string;
  name: string;
}

export default function MeetingNoteDetail() {
  const { noteId } = useParams<{ noteId: string }>();
  const navigate = useNavigate();
  const { getAccessToken, user } = useAuth();
  const { toast } = useToast();
  const { themeColor } = useColor();
  
  const APP_COLOR = appConfig.features.isDemoMode
    ? themeColor
    : appConfig.themeColor;

  // Note state
  const [note, setNote] = useState<MeetingNote | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [templates, setTemplates] = useState<MeetingTemplate[]>([]);

  // User state
  const [currentUser, setCurrentUser] = useState<{
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  } | null>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  // Delete state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load current user
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const token = await getAccessToken();
        const userData = await api.getCurrentUser(token);
        setCurrentUser(userData);
      } catch (error) {
        console.error("Failed to load current user:", error);
      }
    };
    if (user) {
      loadCurrentUser();
    }
  }, [user, getAccessToken]);

  // Load templates
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const token = await getAccessToken();
        const data = await api.getMeetingTemplates(token);
        setTemplates(data.templates || []);
      } catch (error) {
        console.error("Failed to load templates:", error);
      }
    };
    loadTemplates();
  }, [getAccessToken]);

  // Load note detail
  const loadNote = useCallback(async () => {
    if (!noteId) return;
    
    setIsLoading(true);
    try {
      const token = await getAccessToken();
      const data = await api.getMeetingNote(noteId, token);
      setNote(data.meeting_note);
    } catch (error) {
      console.error("Failed to load note:", error);
      toast({
        title: "Fehler",
        description: "Notiz konnte nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [noteId, getAccessToken, toast]);

  useEffect(() => {
    loadNote();
  }, [loadNote]);

  // Delete note
  const handleDelete = async () => {
    if (!noteId) return;
    
    setIsDeleting(true);
    try {
      const token = await getAccessToken();
      await api.deleteMeetingNote(noteId, token);
      toast({
        title: "Erfolg",
        description: "Meetingnotiz wurde gelöscht.",
      });
      navigate("/meeting-notes");
    } catch (error) {
      console.error("Failed to delete note:", error);
      toast({
        title: "Fehler",
        description: "Notiz konnte nicht gelöscht werden.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Format duration
  const formatDuration = (seconds: number | null) => {
    if (!seconds) return "-";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Get template name
  const getTemplateName = (templateId: string | null) => {
    if (!templateId) return null;
    const template = templates.find((t) => t.id === templateId);
    return template?.name || null;
  };

  // Handle profile updates
  const handleProfileImageUpdate = (imageUrl: string) => {
    if (currentUser) {
      setCurrentUser({
        ...currentUser,
        profile_image_url: imageUrl,
      });
    }
  };

  const handleUserInfoUpdate = (updatedUser: {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  }) => {
    setCurrentUser(updatedUser);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-transparent">
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="hidden lg:flex items-center justify-between px-8 py-4 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => navigate("/meeting-notes")}
              className="p-2 rounded-lg hover:bg-gray-100 bg-white border-none"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-xl font-semibold text-gray-900 truncate">
              {isLoading ? "Lädt..." : note?.title || "Meetingnotiz"}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <SettingsDropdown
              onOpenProfileModal={() => setIsProfileModalOpen(true)}
              currentUser={currentUser}
            />
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 lg:p-8">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : !note ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <FileText className="w-12 h-12 mb-4 text-gray-300" />
              <p className="text-lg font-medium">Notiz nicht gefunden</p>
            </div>
          ) : (
            <div className="max-w-4xl mx-auto space-y-6">
              {/* Note Metadata */}
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                    <span className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Dauer: {formatDuration(note.audio_duration)}
                    </span>
                    <span className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {new Date(note.created_at).toLocaleDateString("de-DE", {
                        day: "2-digit",
                        month: "long",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                    {getTemplateName(note.template_id) && (
                      <span className="flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Vorlage: {getTemplateName(note.template_id)}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none active:outline-none text-red-500 hover:text-red-700 transition-colors"
                    title="Notiz löschen"
                  >
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 6H5H21M19 6V20C19 21.1046 18.1046 22 17 22H7C5.89543 22 5 21.1046 5 20V6M8 6V4C8 2.89543 8.89543 2 10 2H14C15.1046 2 16 2.89543 16 4V6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Generated Note */}
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Meetingnotiz</h2>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 prose prose-sm max-w-none text-gray-700">
                  <Markdown remarkPlugins={[remarkGfm]}>
                    {note.generated_note || "Keine Notiz generiert"}
                  </Markdown>
                </div>
              </div>

              {/* Transcript */}
              {note.transcript && (
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <h2 className="text-lg font-medium text-gray-900 mb-4">Transkript</h2>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 max-h-96 overflow-y-auto">
                    {note.transcript.segments && note.transcript.segments.length > 0 ? (
                      <div className="space-y-2">
                        {note.transcript.segments.map((segment, index) => (
                          <div key={index} className="text-sm">
                            {segment.speaker && (
                              <span className="font-medium" style={{ color: APP_COLOR }}>
                                {segment.speaker}:{" "}
                              </span>
                            )}
                            <span className="text-gray-700">{segment.text}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        {note.transcript.text}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Meetingnotiz löschen?
            </h2>
            <p className="text-gray-600 mb-6">
              Sind Sie sicher, dass Sie diese Meetingnotiz löschen möchten? Diese
              Aktion kann nicht rückgängig gemacht werden.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-gray-100 text-gray-700 hover:bg-gray-200 h-10 px-4 py-2"
              >
                Abbrechen
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-white bg-red-600 hover:bg-red-700 h-10 px-4 py-2"
              >
                {isDeleting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Image Modal */}
      <ProfileImageModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        currentUser={currentUser}
        onProfileImageUpdate={handleProfileImageUpdate}
        onUserInfoUpdate={handleUserInfoUpdate}
      />
    </div>
  );
}
